import { Grid, MenuItem, Divider, MyLocationIcon, useTheme } from '@hermes/web-components'
import ConditionalRenderBox from '../../../SearchPage/Alternative/SearchBar/ConditionalRenderBox'
import { LocationSearchTextField, StyledPaper, StyledNoOptionText, ItemRootContainer, ItemTitle } from '../styles'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { HomePageContext } from '../../../../providers/HomePage'
import { InputGradientText } from './styles'
import { FC, useContext, useRef, useState } from 'react'
import { getTextColor } from '.'
import { MappedLocation, OnChangeLocationSearch, SetIsSearchFieldFocused } from '../../../SearchPage/common/types'
import { Location } from '../../../../types'
import { AppDataContext } from '../../../../providers/AppData'
import { useTimeout } from '../../../../hooks/useTimeout'
import useClickOutside from '../../../../hooks/useClickOutside'

interface Props {
  onChangeLocationSearch: OnChangeLocationSearch
  getYourLocation: () => void
  setIsLocationFieldFocused: SetIsSearchFieldFocused
  setIsLocationFieldHovered: SetIsSearchFieldFocused
  isClientSide?: boolean
  isLocationFieldFocused: boolean
  isAnyOptionSelected: boolean
}

const LocationInput: FC<Props> = ({
  onChangeLocationSearch,
  isClientSide,
  isAnyOptionSelected,
  isLocationFieldFocused,
  getYourLocation,
  setIsLocationFieldFocused,
  setIsLocationFieldHovered
}) => {
  const translate = useTranslateMessage()
  const timeout = useTimeout()
  const theme = useTheme()

  const { language } = useContext(AppDataContext)
  const {
    changeCurrentLocation,
    state: { locations, filters, isLocationChanged, isLocationValidationFailed }
  } = useContext(HomePageContext)

  const [searchText, setSearchText] = useState('')
  const locationsAutocompleteRef = useRef(null)

  useClickOutside(locationsAutocompleteRef, () => setIsLocationFieldFocused(false))

  const handleSetFocusOnLocation = () => setIsLocationFieldFocused((prev) => !prev)

  const onLocationChanged = (newValue: { id: string; label: string; slug?: string | null }) =>
    timeout(() => {
      changeCurrentLocation(newValue.id, newValue.label, newValue.slug ?? undefined)
      setSearchText(newValue.label)
    })

  const autocompleteLocationMapper = ({
    place_id,
    description,
    name,
    googlePlaceId,
    slug
  }: Location): MappedLocation => ({ id: place_id || googlePlaceId, label: description || name[language], slug })

  const mappedLocation: MappedLocation[] = locations?.map(autocompleteLocationMapper) || []

  return (
    <Grid item position="relative" width="30%">
      <InputGradientText
        style={{ position: 'absolute', zIndex: 10, left: '14px', top: '14px', marginLeft: '8px' }}
        isActive={isLocationFieldFocused}
        isError={isLocationValidationFailed}
      >
        {translate('homepage.searchbar.where')}
      </InputGradientText>
      <LocationSearchTextField
        ref={locationsAutocompleteRef}
        fullWidth
        value={searchText}
        autoComplete="off"
        onFocus={() => {
          if (!isLocationChanged) {
            handleSetFocusOnLocation()
          }
        }}
        placeholder={translate('search.placeholder.location')}
        isLocationValidationFailed={isLocationValidationFailed}
        isAnyOptionSelected={isAnyOptionSelected}
        onChange={(e) => {
          setIsLocationFieldFocused(true)
          setSearchText(e.target.value)
          onChangeLocationSearch(e)
        }}
        onBlur={() => setSearchText(decodeURIComponent(filters.selectedLocationName ?? ''))}
        onMouseEnter={() => setIsLocationFieldHovered(true)}
        onMouseLeave={() => setIsLocationFieldHovered(false)}
        isLocationFieldFocused={isLocationFieldFocused}
        sx={{
          '& div': {
            paddingRight: isLocationChanged ? '10px !important' : '',
            '& input': {
              marginLeft: '8px',
              marginTop: '20px',
              '::placeholder': {
                color: getTextColor({
                  isError: isLocationValidationFailed,
                  isFocused: isLocationFieldFocused && !!filters.selectedLocationName
                })
              }
            }
          }
        }}
        InputProps={{
          endAdornment: (
            <ConditionalRenderBox isOpen={isLocationFieldFocused} isClientSide={isClientSide}>
              <StyledPaper>
                <ItemRootContainer
                  sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                  onClick={getYourLocation}
                >
                  <ItemTitle>{translate('my_current_location')}</ItemTitle>
                  <MyLocationIcon sx={{ width: 14, height: 14, color: 'primary.main' }} />
                </ItemRootContainer>
                <ItemRootContainer sx={{ py: 0, minHeight: 0 }}>
                  <Divider sx={{ width: '100%' }} />
                </ItemRootContainer>
                {mappedLocation.length ? (
                  mappedLocation.map((location) => (
                    <MenuItem
                      key={`location-link-${location.id}-${location.label}`}
                      onClick={() => {
                        setIsLocationFieldFocused(false)
                        onLocationChanged(location)
                      }}
                      sx={{
                        fontSize: '14px',
                        padding: '6px 16px',
                        color: 'text.primary',
                        boxSizing: 'border-box',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        whiteSpace: 'wrap',
                        '&:hover': { backgroundColor: theme.custom.SearchBar.MenuItem.backgroundColor },
                        ...(decodeURIComponent(filters?.selectedLocationName ?? '') === location.label
                          ? theme.custom.SearchBar.SelectedMenuItem
                          : {})
                      }}
                    >
                      {location.label}
                    </MenuItem>
                  ))
                ) : (
                  <StyledNoOptionText>{translate('searchBar.noOption')}</StyledNoOptionText>
                )}
              </StyledPaper>
            </ConditionalRenderBox>
          )
        }}
      />
    </Grid>
  )
}

export default LocationInput
